'use client';
import React, { useEffect, useState } from 'react';
import { Code2 } from 'lucide-react';

interface DevSession {
  id: string;
  name: string;
  role: string;
  status: 'active' | 'idle' | 'offline';
  lastAction: string;
  duration: string;
}

const statusConfig = {
  active: { dot: 'bg-success', label: 'Active' },
  idle: { dot: 'bg-warning', label: 'Idle' },
  offline: { dot: 'bg-muted-foreground', label: 'Offline' },
};

export default function DeveloperSessionsPanel() {
  const [sessions, setSessions] = useState<DevSession[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSessions = async () => {
      try {
        const res = await fetch((process.env.NEXT_PUBLIC_API_URL || '') + '/api/developer-sessions');
        if (res.ok) {
          const data = await res.json();
          setSessions(data.sessions || []);
        }
      } catch (err) {
        console.error('Failed to fetch developer sessions:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchSessions();
    const interval = setInterval(fetchSessions, 30000);
    return () => clearInterval(interval);
  }, []);

  const activeCount = sessions.filter(s => s.status === 'active').length;

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3.5 border-b border-border">
        <div className="flex items-center gap-2">
          <Code2 size={15} className="text-info" />
          <h3 className="text-sm font-600 text-foreground">Developer Sessions</h3>
        </div>
        <span className="badge-info text-[10px] font-600 px-2 py-0.5 rounded-full">
          {activeCount} active
        </span>
      </div>
      {loading ? (
        <div className="p-4 space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={`skel-dev-${i}`} className="h-12 bg-muted rounded animate-pulse" />
          ))}
        </div>
      ) : sessions.length === 0 ? (
        <div className="px-4 py-8 text-center text-sm text-muted-foreground">No active developer sessions</div>
      ) : (
        <div className="divide-y divide-border">
          {sessions.map((session) => {
            const cfg = statusConfig[session.status] || statusConfig.offline;
            return (
              <div key={session.id} className="px-4 py-3 hover:bg-muted/40 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`w-2 h-2 rounded-full shrink-0 ${cfg.dot} ${session.status === 'active' ? 'pulse-dot' : ''}`} />
                    <div className="min-w-0">
                      <p className="text-[12px] font-600 text-foreground truncate">{session.name}</p>
                      <p className="text-[10px] text-muted-foreground">{session.role}</p>
                    </div>
                  </div>
                  <span className="text-[10px] font-tabular text-muted-foreground shrink-0 ml-2">{session.duration}</span>
                </div>
                <p className="text-[10px] text-muted-foreground mt-1 pl-4 truncate">{session.lastAction}</p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}